import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import { GradientText } from '../gradient-text/GradientText.tsx'
import { gradientPresets } from '../gradient-text/presets.ts'
import type { GradientPreset } from '../gradient-text/presets.ts'
import styles from './StatHighlight.module.css'

export interface StatHighlightProps extends HTMLAttributes<HTMLDivElement> {
  icon?: ReactNode
  value: string
  suffix?: string
  tagline?: string
  gradient?: GradientPreset
  gradientFrom?: string
  gradientTo?: string
  size?: 'sm' | 'md' | 'lg'
  /**
   * Layout variant:
   * - `default`: icon on top, big value, tagline below (most expressive)
   * - `compact`: icon paired with tagline in a header row, value below (data-grid feel)
   */
  layout?: 'default' | 'compact'
  /**
   * Render the value as plain theme-colored text instead of a gradient.
   * Use when readability matters more than visual punch — e.g. data-dense grids
   * where many values compete for attention. The value follows `--nexus-text`
   * so it flips automatically between light and dark themes.
   */
  solid?: boolean
}

export function StatHighlight({
  icon,
  value,
  suffix,
  tagline,
  gradient = 'brand',
  gradientFrom,
  gradientTo,
  size = 'md',
  layout = 'default',
  solid = false,
  className,
  style,
  ...props
}: StatHighlightProps) {
  const preset = gradientPresets[gradient]
  const from = gradientFrom ?? preset.from
  const to = gradientTo ?? preset.to

  const classes = [styles.root, styles[size], styles[layout], className].filter(Boolean).join(' ')

  // Icon is always tinted with the `from` color so the component keeps brand identity
  // even when `solid` mutes the value into theme text.
  const iconStyle: CSSProperties = { color: from }

  const renderedValue = solid
    ? value
    : <GradientText from={from} to={to}>{value}</GradientText>

  if (layout === 'compact') {
    return (
      <div className={classes} style={style} {...props}>
        {(icon || tagline) && (
          <div className={styles.header}>
            {icon && <div className={styles.icon} style={iconStyle}>{icon}</div>}
            {tagline && <p className={styles.tagline}>{tagline}</p>}
          </div>
        )}
        <div className={styles.statGroup}>
          <span className={styles.value}>{renderedValue}</span>
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </div>
      </div>
    )
  }

  return (
    <div className={classes} style={style} {...props}>
      {icon && <div className={styles.icon} style={iconStyle}>{icon}</div>}
      <div className={styles.statGroup}>
        <span className={styles.value}>{renderedValue}</span>
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>
      {tagline && <p className={styles.tagline}>{tagline}</p>}
    </div>
  )
}
